import { useState } from "react";
import { App, Button, Card, Descriptions, Empty, Result, Select, Space, Spin, Table, Tag, Timeline } from "antd";
import type { ItemStatus, TraceTimelineResponse, UnitListRow } from "@shoe-trace/shared";
import { downloadFile, fetchTimeline, lookupUnits } from "../api/client";
import { formatDay, formatWhen, StatusTag } from "../components/widgets";
import { canManage, useSession } from "../session";

const LOOKUP = [
  { value: "code", label: "吊牌或外标" },
  { value: "serial", label: "序列号" },
  { value: "sku", label: "SKU" },
  { value: "tracking", label: "快递单号" },
  { value: "location", label: "库位编码" },
];

export function TraceTimeline(): JSX.Element {
  const { message } = App.useApp();
  const { user } = useSession();
  const manager = canManage(user);
  const [by, setBy] = useState("code");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);
  const [rows, setRows] = useState<UnitListRow[] | null>(null);
  const [profile, setProfile] = useState<TraceTimelineResponse | null>(null);

  async function search(value: string): Promise<void> {
    const query = value.trim();
    if (!query) {
      message.warning("还没输入号码。请扫描或填写后再查。");
      return;
    }
    setLoading(true);
    setMissing(false);
    setRows(null);
    try {
      const result = await lookupUnits(by, query);
      if (result.mode === "none") {
        setProfile(null);
        setMissing(true);
      } else if (result.mode === "many") {
        setProfile(null);
        setRows(result.rows);
      } else {
        setRows(null);
        setProfile(result.item);
      }
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "这次没有查到。");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Space.Compact style={{ width: "100%", maxWidth: 720 }}>
        <Select style={{ width: 160 }} value={by} options={LOOKUP} onChange={setBy} />
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void search(q);
          }}
          placeholder="扫吊牌、外标，或输入快递单号"
          style={{ flex: 1, padding: "4px 11px" }}
        />
        <Button type="primary" loading={loading} onClick={() => void search(q)}>
          查出这双的经历
        </Button>
      </Space.Compact>
      <div style={{ marginTop: 24 }}>
        {loading ? (
          <Spin spinning tip="正在把这双的记录按时间排好…">
            <div style={{ minHeight: 240 }} />
          </Spin>
        ) : null}
        {!loading && !profile && !rows && !missing ? (
          <Empty description="扫吊牌、外标，或输入快递单号。一分钟内应能看到这双经过谁的手。" />
        ) : null}
        {missing ? (
          <Result status="info" title="没有这双的记录" subTitle="请核对扫到的号。若鞋上的吊牌掉了，到异常中心用鞋盒外标或库位加批次找回。" />
        ) : null}
        {rows ? (
          <Table
            rowKey="serial"
            dataSource={rows}
            columns={[
              { title: "序列号", dataIndex: "serial" },
              { title: "SKU", dataIndex: "humanCode" },
              { title: "状态", dataIndex: "status", render: (status: ItemStatus, row: UnitListRow) => <StatusTag status={status} locationCode={row.locationCode} /> },
              { title: "库位", dataIndex: "locationCode", render: (value: string | null, row: UnitListRow) => shelfText(row.status, value) },
              { title: "批次", dataIndex: "batchNo" },
              {
                title: "操作",
                render: (_value: unknown, row: UnitListRow) => (
                  <Button
                    type="link"
                    onClick={() => {
                      void fetchTimeline(row.serial).then(setProfile).catch(() => message.error("这次没有查到。"));
                    }}
                  >
                    看这双的经历
                  </Button>
                ),
              },
            ]}
          />
        ) : null}
        {profile ? <ProfileView item={profile} canDownload={manager} /> : null}
      </div>
    </>
  );
}

function ProfileView({ item, canDownload }: { item: TraceTimelineResponse; canDownload: boolean }): JSX.Element {
  const { message } = App.useApp();
  const shoe = item.item;
  return (
    <>
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 2, md: 3 }}
        items={[
          { key: "id", label: "吊牌身份", children: `${shoe.serial} / ${shoe.internalCode}` },
          { key: "sku", label: "SKU", children: shoe.humanCode },
          { key: "status", label: "状态", children: <StatusTag status={shoe.status} locationCode={shoe.locationCode} /> },
          { key: "color", label: "颜色", children: shoe.color },
          { key: "batch", label: "批次", children: shoe.batchNo },
          { key: "factory", label: "代工厂", children: shoe.factoryName ?? "—" },
          { key: "in", label: "入库日", children: formatDay(shoe.inboundDate) },
          { key: "loc", label: "当前库位", children: shelfText(shoe.status, shoe.locationCode) },
          { key: "who", label: "最后操作人", children: shoe.operatorName ?? "—" },
          { key: "track", label: "快递单号", children: shoe.trackingNo ?? "—" },
        ]}
      />
      {canDownload ? (
        <Button
          style={{ margin: "16px 0" }}
          onClick={() => {
            void downloadFile(`/api/units/${shoe.serial}/timeline.pdf`, `${shoe.serial}.pdf`).then(() => {
              message.success("举证文件已下载。里面是只读记录，不能在文件里改。");
            });
          }}
        >
          下载这双的举证文件
        </Button>
      ) : null}
      <Card title="从入库到现在">
        {item.logs.length === 0 ? (
          <Empty description="这双有档案，但还没有任何操作记录。" />
        ) : (
          <Timeline
            mode="left"
            items={item.logs.map((log) => ({
              label: formatWhen(log.createdAt),
              color: eventColor(log.action),
              children: (
                <div>
                  <Tag>{log.title}</Tag>
                  <div>{log.description ?? log.title}</div>
                  <div>{log.operatorName}{log.location ? ` · ${log.location}` : ""}</div>
                </div>
              ),
            }))}
          />
        )}
      </Card>
    </>
  );
}

function shelfText(status: ItemStatus, code: string | null): string {
  if (code) return code;
  if (status === "PRINTED") return "待入库";
  if (status === "IN_STOCK" || status === "PICKED") return "待上架";
  if (status === "SHIPPED") return "已出库";
  return "—";
}

function eventColor(action: string): string {
  if (action === "FIFO_BLOCKED" || action === "LABEL_VOID" || action === "FROZEN") return "red";
  if (action === "VERIFY_PASS" || action === "PACKED" || action === "SHIPPED") return "green";
  if (action === "RETURNED") return "gray";
  return "blue";
}
